import type { Freshness, PersonResponse } from '@pulse/contracts';
import * as React from 'react';
import { Link } from 'react-router-dom';

import { IncompleteMark } from '@/components/pulse/freshness';
import { ArrowIcon } from '@/components/pulse/icons';
import { SourceLink } from '@/components/pulse/source-link';

/** PER-3's Built this week, as the prototype's `thisWeek()` draws it; Talos's page shows it too (TLS-4). */
export function BuiltThisWeek({ thisWeek, freshness }: { readonly thisWeek: PersonResponse['thisWeek']; readonly freshness: Freshness | null }): React.ReactElement {
  return (
    <section className="card pad stack" style={{ gap: 12 }}>
      <div className="row" style={{ justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <h2>
          Built this week <IncompleteMark figure="built" freshness={freshness} />
        </h2>
        <Link className="linkish" to="/trends" style={{ fontSize: 13 }}>
          Compare weeks <ArrowIcon />
        </Link>
      </div>
      <div className="row num" style={{ gap: 24, flexWrap: 'wrap' }}>
        <span className="stack">
          <b style={{ fontSize: 26 }}>{thisWeek.built}</b>
          <span style={{ fontSize: 12, color: 'var(--muted)' }}>built · {thisWeek.lastWeek} last week</span>
        </span>
        <span className="stack">
          <b style={{ fontSize: 26, color: 'var(--accent-ink)' }}>{thisWeek.finished}</b>
          <span style={{ fontSize: 12, color: 'var(--muted)' }}>finished</span>
        </span>
        <span className="stack">
          <b style={{ fontSize: 26 }}>{thisWeek.open}</b>
          <span style={{ fontSize: 12, color: 'var(--muted)' }}>still open</span>
        </span>
        <span className="stack">
          <b style={{ fontSize: 26, color: thisWeek.unlinkedFlagged ? 'var(--warn-ink)' : 'var(--ink)' }}>{thisWeek.unlinked}</b>
          <span style={{ fontSize: 12, color: 'var(--muted)' }}>no story or task</span>
        </span>
      </div>
      <div className="stack">
        {thisWeek.items.map((item) => (
          <SourceLink key={`${item.ref}${item.url}`} className="prow" href={item.url} style={{ textDecoration: 'none', color: 'var(--ink)', gridTemplateColumns: '96px minmax(0,1fr) auto' }}>
            <span className="mono" style={{ fontSize: 13, fontWeight: 500, color: item.kind === 'No story or task' ? 'var(--muted)' : 'var(--accent-ink)' }}>
              {item.ref}
            </span>
            <span className="stack" style={{ gap: 2 }}>
              <span>{item.title}</span>
              <span className="mono" style={{ fontSize: 12, color: 'var(--muted)' }}>
                {item.repositoryName ?? 'Teamwork'}
                {item.kind === 'No story or task' ? ' · no story or task' : ''}
              </span>
            </span>
            {item.finished ? <span className="chip c-merged">Finished</span> : <span className="chip c-review">Open</span>}
          </SourceLink>
        ))}
      </div>
      <span style={{ fontSize: 13, color: 'var(--muted)' }}>
        {thisWeek.items.length === 0 ? 'Nothing built this week yet.' : `Showing ${thisWeek.items.length} of ${thisWeek.built}, linked items first.`}
      </span>
    </section>
  );
}
